import { createQueryKeys, inferQueryKeys } from '@lukemorales/query-key-factory';
import { AxiosResponse } from 'axios';

import {
  $httpRootApi,
  AnyErrorResponse,
  UpdateCacheCallback,
  updateQueryCache,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@core/http-client';
import { getQueriesAsSearch } from '@shared/utils/common';

import {
  PropertiesListPayload,
  PropertiesListResponse,
  PropertiesLookupPayload,
  PropertiesLookupResponse,
  PropertyAssociateCompanyParams,
  PropertyCreateCommentPayload,
  PropertyCreatePayload,
  PropertyShowItem,
  PropertySpacesResponse,
  PropertyUpdatePayload,
} from './propertiesApi.types';

export const propertiesQueryKeys = createQueryKeys('properties', {
  all: null,
  list: (payload: PropertiesListPayload) => ({
    queryKey: [payload.viewType || '', payload.query],
    queryFn: async ({ signal }) => {
      return (await $httpRootApi.get(`/properties${getQueriesAsSearch(payload.query)}`, { signal })).data;
    },
  }),
  propertiesLookupList: (payload: PropertiesLookupPayload) => ({
    queryKey: [payload.query],
    queryFn: async () => (await $httpRootApi.get(`/properties/lookup${getQueriesAsSearch(payload.query)}`)).data,
  }),
  show: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/properties/${id}`)).data,
  }),
  spacesList: (payload: Id) => ({
    queryKey: [payload],
    queryFn: async () => (await $httpRootApi.get<PropertySpacesResponse>(`/properties/${payload}/spaces`)).data,
  }),
});

export type PropertiesQueryKeys = inferQueryKeys<typeof propertiesQueryKeys>;

// -- Queries --

export const usePropertiesListQuery = (
  payload: PropertiesListPayload,
  options?: UseQueryOptions<PropertiesListResponse>
) =>
  useQuery<PropertiesQueryKeys['list']['queryKey'], PropertiesListResponse>({
    ...propertiesQueryKeys.list(payload),
    keepPreviousData: true,
    ...options,
  });

export const usePropertiesShowQuery = (id: string, options?: UseQueryOptions<PropertyShowItem>) =>
  useQuery<PropertiesQueryKeys['show']['queryKey'], PropertyShowItem>({
    ...propertiesQueryKeys.show(id),
    ...options,
  });

export const usePropertiesLookupList = (
  payload: PropertiesLookupPayload,
  options?: UseQueryOptions<PropertiesLookupResponse>
) =>
  useQuery<PropertiesQueryKeys['propertiesLookupList']['queryKey'], PropertiesLookupResponse>({
    ...propertiesQueryKeys.propertiesLookupList(payload),
    keepPreviousData: true,
    ...options,
  });

export const usePropertySpaceList = (payload: number, options?: UseQueryOptions<PropertySpacesResponse>) =>
  useQuery<PropertiesQueryKeys['spacesList']['queryKey'], PropertySpacesResponse>({
    ...propertiesQueryKeys.spacesList(payload),
    keepPreviousData: true,
    ...options,
  });

// -- Mutations --

export const usePropertyCreateMutation = (
  opts?: UseMutationOptions<Partial<PropertyCreatePayload>, PropertyShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<PropertyCreatePayload['body'], AxiosResponse<PropertyShowItem>>(
          `/properties`,
          data.body
        )
      ).data,
  });

export const usePropertyCreateCommentMutation = (
  opts?: UseMutationOptions<PropertyCreateCommentPayload, PropertyShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<PropertyCreateCommentPayload['body'], AxiosResponse<PropertyShowItem>>(
          `/properties/${data.id}/comment`,
          data.body
        )
      ).data,
  });

export const usePropertyUpdateMutation = (
  opts?: UseMutationOptions<PropertyUpdatePayload, PropertyShowItem, AnyErrorResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<PropertyUpdatePayload['body'], AxiosResponse<PropertyShowItem>>(
          `/properties/${data.id}`,
          data.body
        )
      ).data,
  });

export const usePropertyDeleteMutation = (opts?: UseMutationOptions<Id>) =>
  useMutation({
    ...opts,
    mutationFn: (id) => $httpRootApi.delete(`/properties/${id}`),
  });

export const usePropertyAssociateCompanyMutation = (
  opts?: UseMutationOptions<PropertyAssociateCompanyParams, PropertyShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<undefined, AxiosResponse<PropertyShowItem>>(
          `/properties/${data.propertyId}/companies/${data.role}/${data.companyId}`
        )
      ).data,
  });

export const usePropertyDisassociateCompanyMutation = (
  opts?: UseMutationOptions<PropertyAssociateCompanyParams, PropertyShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<undefined, AxiosResponse<PropertyShowItem>>(
          `/properties/${data.propertyId}/companies/${data.role}/${data.companyId}`
        )
      ).data,
  });

// ---- Utils ----

export const updatePropertyShowCache = (id: string, callback: UpdateCacheCallback<PropertyShowItem>) =>
  updateQueryCache<PropertyShowItem>(propertiesQueryKeys.show(id).queryKey, callback);
